import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Tabs, Tab, Box, CircularProgress } from "@mui/material";
import { useAppDispatch } from "../../../hooks/useAppDispatch.tsx";
import { fetchImages } from "../../../redux/features/imageManagement/getUploadedImagesSlice.tsx";
import {
  uploadImage,
  updateImageInfo,
  deleteImage,
} from "../../../redux/features/imageManagement/imageManagementSlice.tsx";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../hooks/useAppSelector.tsx";

const tutorialTabs = ["screen_1", "screen_2"];

const ImageManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [selectedTutorialTab, setSelectedTutorialTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const dispatch = useAppDispatch();
  const { imagesList, isLoading } = useAppSelector((state) => state.imageList);

  useEffect(() => {
    dispatch(fetchImages());
  }, [dispatch]);

  const handleModalClose = () => setIsModalOpen(false);

  const getSelectedTutorialImage = () => {
    const tutorialImages = imagesList["tutorials"] as { [key: string]: string };
    const selectedScreen = `screen_${selectedTutorialTab + 1}`;
    return tutorialImages?.[selectedScreen];
  };

  const handleTutorialTabChange = (
    _: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedTutorialTab(newValue);
  };

  const handleUploadClick = (title: string) => {
    setModalTitle(title);
    setIsModalOpen(true);
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/svg+xml",
        "image/webp",
      ];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPEG, PNG, JPG, SVG, and WEBP files are allowed");
        return;
      }

      if (file.size > maxSize) {
        toast.error("File size exceeds 5MB limit");
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }
    setIsUploading(true);

    try {
      const imageUrlToDelete =
        imagesList[modalTitle.toLowerCase().replace(/\s+/g, "-")]?.[0];
      if (imageUrlToDelete) {
        await dispatch(deleteImage(imageUrlToDelete)).unwrap();
      }

      const result = await dispatch(
        uploadImage({
          file: selectedFile,
          fileName: selectedFile.name,
          mimeType: selectedFile.type,
        })
      ).unwrap();
      const slug = modalTitle.toLowerCase().replace(/\s+/g, "-");
      const screenName = modalTitle.includes("Tutorials")
        ? tutorialTabs[selectedTutorialTab]
        : undefined;

      await dispatch(
        updateImageInfo({
          imageUrl: result.imageUrl,
          slug: modalTitle.includes("Tutorials") ? "tutorials" : slug,
          screenName,
        })
      ).unwrap();

      dispatch(fetchImages());
      toast.success("File uploaded successfully!");
      handleModalClose();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("File upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const formatLabel = (tab) => {
    return tab
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleUploadAreaClick = () => fileInputRef.current?.click();

  return (
    <Box sx={{ padding: "24px" }}>
      {/* Welcome Screen Section */}
      <Box mb={4} mt={2}>
        <div className="flex justify-between items-center">
          <h2 className="text-[24px] font-normal text-[#004135]">
            Welcome Screen
          </h2>
          <Button
            variant="contained"
            style={{ backgroundColor: "#3BC1CD", color: "#FFF" }}
            onClick={() => handleUploadClick("welcome-screen")}
          >
            Upload Image
          </Button>
        </div>
        <ImagePreview
          isLoading={isLoading}
          imageUrl={
            Array.isArray(imagesList["welcome-screen"])
              ? imagesList["welcome-screen"]?.[0]
              : undefined
          }
        />
      </Box>
      <hr />

      {/* Tutorials Section */}
      <Box mb={4} mt={2}>
        <div className="flex justify-between items-center">
          <h2 className="text-[24px] font-normal text-[#004135]">Tutorials</h2>
          <Button
            variant="contained"
            style={{ backgroundColor: "#3BC1CD", color: "#FFF" }}
            onClick={() =>
              handleUploadClick(
                `Tutorials - ${tutorialTabs[selectedTutorialTab]}`
              )
            }
          >
            Upload Image
          </Button>
        </div>
        <Tabs
          value={selectedTutorialTab}
          onChange={handleTutorialTabChange}
          sx={{ marginTop: "10px" }}
        >
          {tutorialTabs.map((tab, index) => (
            <Tab
              key={index}
              label={formatLabel(tab)}
              style={{
                backgroundColor:
                  selectedTutorialTab === index ? "#3BC1CD" : "#FFF",
                color: selectedTutorialTab === index ? "#FFF" : "#000",
                borderRadius: "8px",
                fontWeight: selectedTutorialTab === index ? "700" : "500",
                textTransform: "none",
                padding: "10px 16px",
                marginRight: "12px",
              }}
            />
          ))}
        </Tabs>
        <ImagePreview
          isLoading={isLoading}
          imageUrl={getSelectedTutorialImage()}
        />
      </Box>
      <hr />

      {/* Home Backdrop Section */}
      <Box mb={4} mt={2}>
        <div className="flex justify-between items-center">
          <h2 className="text-[24px] font-normal text-[#004135]">
            Home Backdrop
          </h2>
          <Button
            variant="contained"
            style={{ backgroundColor: "#3BC1CD", color: "#FFF" }}
            onClick={() => handleUploadClick("home-backdrop")}
          >
            Upload Image
          </Button>
        </div>
        <ImagePreview
          isLoading={isLoading}
          imageUrl={
            Array.isArray(imagesList["home-backdrop"])
              ? imagesList["home-backdrop"]?.[0]
              : undefined
          }
        />
      </Box>
      <hr />

      {/* Know More Section */}
      <Box mb={4} mt={2}>
        <div className="flex justify-between items-center">
          <h2 className="text-[24px] font-normal text-[#004135]">Know More</h2>
          <Button
            variant="contained"
            style={{ backgroundColor: "#3BC1CD", color: "#FFF" }}
            onClick={() => handleUploadClick("know-more")}
          >
            Upload Image
          </Button>
        </div>
        <ImagePreview
          isLoading={isLoading}
          imageUrl={
            Array.isArray(imagesList["know-more"])
              ? imagesList["know-more"]?.[0]
              : undefined
          }
        />
      </Box>

      {/* Modal for Upload */}
      <Modal open={isModalOpen} onClose={handleModalClose}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#FFF",
            padding: "20px",
            borderRadius: "10px",
          }}
          sx={{ width: { xs: "350px", md: "612px" } }}
        >
          <h2 className="text-center text-[32px] font-bold text-[#3D3A3A] mb-7">
            {formatLabel(modalTitle)}
          </h2>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/jpg, image/jpeg, image/png, image/svg+xml, image/webp"
            onChange={handleFileSelect}
          />
          <Box
            onClick={handleUploadAreaClick}
            style={{
              border: "2px dashed #C4C4C4",
              borderRadius: "10px",
              padding: "43px",
              backgroundColor: "#A9EDF34D",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {filePreview ? (
              <img
                src={filePreview}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <>
                <img src="/assets/fileUpload-icon.png" alt="upload icon" />
                <p className="text-sm font-bold">Upload Image</p>
                <span className="text-xs font-normal text-[#3BC1CD] text-center">
                  Maximum size allowed: 5 MB, <br /> Format supported: JPEG,
                  PNG, JPG, SVG, and WEBP only
                </span>
              </>
            )}
          </Box>
          <Box mt={3} display="flex" gap={1}>
            <Button
              variant="outlined"
              style={{ flex: "1" }}
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{
                backgroundColor: "#3BC1CD",
                color: "#FFF",
                flex: "1",
                opacity: selectedFile ? 1 : 0.5,
              }}
              onClick={handleFileUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Confirm"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

const ImagePreview = ({
  imageUrl,
  isLoading,
}: {
  imageUrl?: string;
  isLoading?: boolean;
}) => {
  // Function to extract filename from URL
  const getImageName = (url?: string) => {
    if (!url) return "No image selected";
    const parts = url.split("/");
    const fileName = parts[parts.length - 1];
    return decodeURIComponent(fileName);
  };
  const cloudFrontUrl = process.env.REACT_APP_AWS_CLOUD_FRONT;

  const getImageSrc = (cloudFrontUrl?: string, imageUrl?: string) => {
    if (!imageUrl) {
      return "/assets/no-image.png";
    }
    return `${cloudFrontUrl || ""}${imageUrl}`;
  };

  return (
    <Box
      style={{
        width: "255px",
        height: "230px",
        border: "1px solid #C4C4C4",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        // alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#FFF",
        marginTop: "31px",
        marginBottom: "31px",
      }}
    >
      <Box
        style={{
          width: "100%",
          overflow: "hidden",
          borderRadius: "6px",
          minHeight: "170px",
        }}
      >
        {!isLoading ? (
          <img
            // src={`${cloudFrontUrl + imageUrl}` || "/assets/no-image.png"}
            src={getImageSrc(cloudFrontUrl, imageUrl)}
            alt="Rooms"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: imageUrl ? "none" : "grayscale(100%)",
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
      <Box style={{ padding: "12px" }}>
        <p
          style={{
            fontSize: "16px",
            fontWeight: "400",
            margin: 0,
            color: "#333",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {getImageName(imageUrl)}{" "}
        </p>
        {/* <p
          style={{
            fontSize: "12px",
            margin: 0,
            color: "#3BC1CD",
            fontWeight: "700",
          }}
        >
          Selected
        </p> */}
      </Box>
    </Box>
  );
};

export default ImageManagement;
